import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CreateGalleryPresetBody,
  GalleryPresetDto,
  StandardGalleryPresetDto,
  UpdateGalleryPresetBody,
} from '@nusign/picflow-backend-types';
import { combineLatest, forkJoin, from, map, Observable, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';
import { TenantService } from './tenant.service';

@Injectable({
  providedIn: 'root',
})
export class GalleryPresetService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private tenantService = inject(TenantService);
  private state = inject(StateService);

  private getAllCustomGalleryPresets() {
    return this.loader.get('custom_gallery_presets', () =>
      this.http
        .get<{ data: GalleryPresetDto[] }>(`${this.config.apiEndpoint}/gallery_presets`)
        .pipe(map(resp => resp.data))
    );
  }

  public getCustomGalleryPresets(): Observable<GalleryPresetDto[]> {
    return this.getAllCustomGalleryPresets().pipe(
      map(presets => {
        return presets
          .filter(p => !p['type'] || p['type'] === 'custom')
          .sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
      })
    );
  }

  public getStandardGalleryPresets(
    includeNotVisible = false
  ): Observable<(StandardGalleryPresetDto & { visible: boolean })[]> {
    return this.loader
      .get('standard_gallery_presets', () =>
        this.http.get<{ data: StandardGalleryPresetDto[] }>(`${this.config.apiEndpoint}/gallery_presets/standard`)
      )
      .pipe(
        withLatestFrom(this.state.activeTenant$),
        map(([resp, activeTenant]) => {
          return resp.data
            .map(p => {
              const visible = (activeTenant.onboarding_checklist['PRESET_VISIBLE_' + p.id] as boolean) ?? true;
              return { ...p, visible };
            })
            .filter(p => includeNotVisible || p.visible);
        })
      );
  }

  public getDefaultPreset(): Observable<GalleryPresetDto | StandardGalleryPresetDto> {
    return combineLatest([
      this.getCustomGalleryPresets(),
      this.getStandardGalleryPresets(),
      this.state.activeTenant$,
    ]).pipe(
      map(([customPresets, standardPresets, activeTenant]) => {
        const presets = [...customPresets, ...standardPresets];
        const id = activeTenant.onboarding_checklist['DEFAULT_PRESET'];
        if (id) return presets.find(p => p.id === id);
        return customPresets.find(p => p.is_default) || standardPresets.find(p => p.id === 'review');
      })
    );
  }

  public createPreset(preset: CreateGalleryPresetBody) {
    return this.http.post<GalleryPresetDto>(`${this.config.apiEndpoint}/gallery_presets`, preset).pipe(
      map(resp => {
        this.loader.update<GalleryPresetDto[]>('custom_gallery_presets', presets => {
          if (!presets) return;
          presets.push(resp);
          return presets;
        });
        return resp;
      })
    );
  }

  public deletePreset(id: string) {
    return this.http.delete(`${this.config.apiEndpoint}/gallery_presets/${id}`).pipe(
      tap(() => {
        this.loader.update<GalleryPresetDto[]>('custom_gallery_presets', presets => {
          if (!presets) return;
          return presets.filter(p => p.id !== id);
        });
      })
    );
  }

  public updatePreset(id: string, params: UpdateGalleryPresetBody): Observable<void> {
    return this.http
      .patch<GalleryPresetDto | StandardGalleryPresetDto>(`${this.config.apiEndpoint}/gallery_presets/${id}`, params)
      .pipe(
        tap(() => {
          this.loader.update<GalleryPresetDto[]>('custom_gallery_presets', presets => {
            if (!presets) return;
            return presets.map(p => (p.id === id ? { ...p, ...params } : p));
          });
        }),
        map(() => undefined)
      );
  }

  public setPresetVisibility(id: string, visible: boolean) {
    return forkJoin([this.getStandardGalleryPresets(true).pipe(take(1)), this.state.activeTenant$.pipe(take(1))]).pipe(
      switchMap(([standardPresets, activeTenant]) => {
        const standardPreset = standardPresets.find(p => p.id === id);
        if (standardPreset) {
          return this.tenantService.updateTenant({
            onboarding_checklist: {
              ...activeTenant.onboarding_checklist,
              ['PRESET_VISIBLE_' + id]: visible,
            },
          });
        }

        return this.updatePreset(id, { visible });
      })
    );
  }

  public makeDefault(id: string) {
    const activeTenant = this.state.activeTenant$.value;
    return from(
      this.tenantService.updateTenant({
        onboarding_checklist: {
          ...activeTenant.onboarding_checklist,
          DEFAULT_PRESET: id,
        },
      })
    );
  }
}
